import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Download, Filter, Sparkles, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { asArray } from "@/lib/asArray";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { sourceChip } from "@/components/AddedByAuditorBadge";   // PR 46 review · D1: the auditor's rows — own colour, EN + AR label

const SOURCE_COLORS = {
  "Auto (Code Match)": "bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300",
  "Auto (Keyword Match)": "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  "Auto (AI)": "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-300",
  "Manual": "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  "Manual (Overridden)": "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  "Unmapped": "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300",
};

export default function CoaMappingTable({ engagementId, coa, reload }) {
  const colW2 = useResizableColumns({ storageKey: "coa-mapping-ai-suggestions", defaults: [100, 110, 160, 130], mins: [60, 72, 100, 90] });   // Step 11d · add-on
  const colW = useResizableColumns({ storageKey: "coa-mapping", defaults: [100, 110, 240, 160, 160, 110, 160, 240, 240], mins: [60, 72, 130, 100, 100, 72, 100, 130, 130] });   // Step 11d · add-on
  const { lang, dict, t } = useI18n();
  const [taxonomy, setTaxonomy] = useState({ major_categories: [], mapping_options: {} });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ major: "", mapping: "" });
  const [applying, setApplying] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSug, setAiSug] = useState([]);
  const [aiPicked, setAiPicked] = useState(new Set());

  useEffect(() => {
    api.get("/coa-taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "unmapped") return coa.filter((c) => !c.major_category || !c.mapping);
    if (filter === "auto") return coa.filter((c) => (c.mapping_source || "").startsWith("Auto"));
    if (filter === "manual") return coa.filter((c) => (c.mapping_source || "").startsWith("Manual"));
    return coa;
  }, [coa, filter]);

  const unmappedCount = useMemo(
    () => coa.filter((c) => !c.major_category || !c.mapping).length,
    [coa],
  );

  const toggleRow = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  };

  // Batch 28 · Follow-up 1 · F2 (b): major category, mapping and note reference are row drafts — one Save / Cancel bar under the
  // table; Save sends only the changed accounts, each on its own (PUT /coa/{id} for the mapping, PATCH /coa/{id}/note for the
  // note reference); a failed account stays highlighted with its error. Apply to selected (bulk), AI suggest / accept and the
  // template download stay immediate actions.
  const td = useTableDraft({
    rows: coa, reload,
    saveRow: async (id, patch) => {
      const { note_reference, ...rest } = patch;
      if (Object.keys(rest).length) {
        const payload = { ...rest };
        if (rest.major_category !== undefined && rest.mapping === undefined) payload.mapping = "";
        await api.put(`/coa/${id}`, payload);
      }
      if (note_reference !== undefined) {
        const fd = new FormData();
        fd.append("note_reference", note_reference);
        await api.patch(`/coa/${id}/note`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
    },
  });
  const saveRow = (row, patch) => {
    if (patch.major_category !== undefined) { td.edit(row.id, "major_category", patch.major_category); td.edit(row.id, "mapping", ""); }
    if (patch.mapping !== undefined) td.edit(row.id, "mapping", patch.mapping);
  };

  const applyBulk = async () => {
    if (!bulk.major || !bulk.mapping || selected.size === 0) {
      toast.error(t("coaMapping.toastPickCategoryRow"));
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/coa/bulk-update", {
        mapping_ids: Array.from(selected),
        major_category: bulk.major,
        mapping: bulk.mapping,
      });
      toast.success(t("coaMapping.toastAppliedRows").replace("{rows}", fmtPlural(r.data.updated, lang, dict.plurals.rows)));
      setBulkOpen(false);
      setSelected(new Set());
      setBulk({ major: "", mapping: "" });
      await reload();
    } catch (e) {
      toast.error(toErrMsg(e, t("coaMapping.toastBulkFailed")));
    } finally { setApplying(false); }
  };

  const askAI = async () => {
    setAiLoading(true);
    setAiSug([]); setAiPicked(new Set());
    try {
      const r = await api.post(`/coa/${engagementId}/ai-suggest`);
      const sug = r.data.suggestions || [];
      setAiSug(sug);
      setAiPicked(new Set(sug.filter((s) => s.confidence >= 0.7).map((s) => s.id)));
      const stillUnmapped = (r.data.unsuggested || []).length;
      if (sug.length === 0) {
        toast.info(r.data.message || t("coaMapping.toastNoSuggestions"));
      } else if (stillUnmapped > 0) {
        toast.warning(
          t("coaMapping.toastAiWarning")
            .replace("{ok}", sug.length)
            .replace("{total}", r.data.unmapped_count)
            .replace("{left}", stillUnmapped),
          { duration: 8000 });
      }
    } catch (e) {
      toast.error(toErrMsg(e, t("coaMapping.toastAiSuggestFail")));
    } finally { setAiLoading(false); }
  };

  const acceptAI = async () => {
    const picked = aiSug.filter((s) => aiPicked.has(s.id));
    if (picked.length === 0) { toast.error(t("coaMapping.toastNothingPicked")); return; }
    let done = 0;
    for (const s of picked) {
      try {
        await api.put(`/coa/${s.id}`, {
          major_category: s.major_category, mapping: s.mapping,
        });
        done++;
      } catch { /* skip */ }
    }
    toast.success(t("coaMapping.toastApplied").replace("{n}", fmtPlural(done, lang, dict.plurals.aiSuggestions)));
    setAiOpen(false); setAiSug([]); setAiPicked(new Set());
    await reload();
  };

  const openAI = () => { setAiOpen(true); askAI(); };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("af_token");
    const r = await fetch(`${API_URL}/coa-template`,
                          { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tb_mapping_template.xlsx";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const allChecked = filtered.length > 0 && selected.size === filtered.length;

  return (
    <Card data-testid="coa-mapping-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base">{t("coaMapping.title")}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("coaMapping.descPre")}<b>{t("coaMapping.descMajorBold")}</b>{t("coaMapping.descAnd")}<b>{t("coaMapping.descMappingBold")}</b>{t("coaMapping.descPost")}
            {unmappedCount > 0 && (
              <span className="ms-2 rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                {t("coaMapping.unmappedBadge").replace("{n}", unmappedCount)}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button size="sm" onClick={() => setBulkOpen(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="coa-bulk-apply-btn">
              <Wand2 className="me-1 h-3 w-3" /> {t("coaMapping.applySelected").replace("{n}", selected.size)}
            </Button>
          )}
          {unmappedCount > 0 && (
            <Button size="sm" variant="outline" onClick={openAI}
                    className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                    data-testid="coa-ai-suggest-btn">
              <Sparkles className="me-1 h-3 w-3" /> {t("coaMapping.aiSuggestBtn").replace("{n}", unmappedCount)}
            </Button>
          )}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-40" data-testid="coa-filter">
              <Filter className="me-1 h-3 w-3" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("coaMapping.filterAll")}</SelectItem>
              <SelectItem value="unmapped">{t("coaMapping.filterUnmapped")}</SelectItem>
              <SelectItem value="auto">{t("coaMapping.filterAuto")}</SelectItem>
              <SelectItem value="manual">{t("coaMapping.filterManual")}</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={downloadTemplate}
                  data-testid="coa-template-download">
            <Download className="me-1 h-3 w-3" /> {t("coaMapping.template")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <ResizableTable cols={colW} className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/60">
            <tr>
              <ResizableTh index={0} className="w-8 px-2 py-2">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll}
                          data-testid="coa-select-all" />
              </ResizableTh>
              <ResizableTh index={1} className="px-3 py-2 text-start">{t("coaMapping.colCode")}</ResizableTh>
              <ResizableTh index={2} className="px-3 py-2 text-start">{t("coaMapping.colRawName")}</ResizableTh>
              <ResizableTh index={3} className="px-3 py-2 text-start">{t("coaMapping.colMajor")}</ResizableTh>
              <ResizableTh index={4} className="px-3 py-2 text-start">{t("coaMapping.colMapping")}</ResizableTh>
              <ResizableTh index={5} className="px-3 py-2 text-start">{t("coaMapping.colSource")}</ResizableTh>
              <ResizableTh index={6} className="px-3 py-2 text-start">
                {t("coaMapping.colFsMapping")}
                <Link to={`/ifrs/${engagementId}/mapping`}
                      className="ms-1 text-[10px] font-normal text-emerald-600 hover:underline dark:text-emerald-400"
                      data-testid="coa-fs-mapping-link">
                  {t("coaMapping.editLink")}
                </Link>
              </ResizableTh>
              <ResizableTh index={7} className="px-3 py-2 text-start">{t("coaMapping.colNoteMapping")}</ResizableTh>
              <ResizableTh index={8} className="px-3 py-2 text-start">{t("coaMapping.colNoteRef")}</ResizableTh>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">
                {coa.length === 0 ? t("coaMapping.noAccountsYet") : t("coaMapping.noRowsMatch")}
              </td></tr>
            )}
            {filtered.map((c0) => {
              const c = td.rowValue(c0);
              // Batch 3 · 3.9 — legacy rows stored as "Manual (Overridden)" read "Manual"
              const source = (c.mapping_source === "Manual (Overridden)" ? "Manual" : c.mapping_source) || (c.major_category ? "Manual" : "Unmapped");
              const chip = sourceChip(c, source, SOURCE_COLORS, lang);
              const mappingOpts = taxonomy.mapping_options[c.major_category] || [];
              const isSel = selected.has(c.id);
              return (
                <tr key={c.id} className={`border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 ${td.rowClass(c.id) || (!c.major_category ? "bg-red-50/30 dark:bg-red-950/10" : "")} ${isSel ? "bg-emerald-50/40 dark:bg-emerald-950/20" : ""}`}
                    data-changed={td.isChanged(c.id) ? "1" : "0"} data-failed={td.errorOf(c.id) ? "1" : "0"}
                    data-testid={`coa-row-${c.id}`}>
                  <td className="px-2 py-2 text-center">
                    <Checkbox checked={isSel} onCheckedChange={() => toggleRow(c.id)}
                              data-testid={`coa-select-${c.id}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{c.raw_account_code || "—"}</td>
                  <td className="px-3 py-2">{c.raw_account_name}<RowSaveError draft={td} id={c.id} testid={`coa-row-error-${c.id}`} /></td>
                  <td className="px-3 py-2">
                    {c.fs_mapping ? (
                      /* Derived from fs_mapping — read-only, cannot disagree. */
                      <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-800 dark:bg-slate-900/60"
                           title={t("coaMapping.derivedTooltip").replace("{code}", c.fs_mapping)}
                           data-testid={`coa-major-derived-${c.id}`}>
                        <span className="text-[11px]">{c.major_category || "—"}</span>
                        <Link to={`/ifrs/${engagementId}/mapping`}
                              className="text-[9px] font-medium text-emerald-600 hover:underline dark:text-emerald-400">
                          {t("coaMapping.derivedLink")}
                        </Link>
                      </div>
                    ) : (
                      <Select value={c.major_category || ""}
                              onValueChange={(v) => saveRow(c, { major_category: v })}
                              disabled={td.saving}>
                        <SelectTrigger className="h-8 w-44" data-testid={`coa-major-${c.id}`}>
                          <SelectValue placeholder={t("coaMapping.selectDash")} />
                        </SelectTrigger>
                        <SelectContent>
                          {taxonomy.major_categories.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {c.fs_mapping ? (
                      /* Same source-of-truth as Major Category above. */
                      <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-800 dark:bg-slate-900/60"
                           title={t("coaMapping.derivedTooltip").replace("{code}", c.fs_mapping)}
                           data-testid={`coa-mapping-derived-${c.id}`}>
                        <span className="text-[11px]">{c.mapping || "—"}</span>
                      </div>
                    ) : (
                      <Select value={c.mapping || ""}
                              onValueChange={(v) => saveRow(c, { mapping: v })}
                              disabled={!c.major_category || td.saving}>
                        <SelectTrigger className="h-8 w-64" data-testid={`coa-mapping-${c.id}`}>
                          <SelectValue placeholder={c.major_category ? t("coaMapping.selectDash") : t("coaMapping.pickCategoryFirst")} />
                        </SelectTrigger>
                        <SelectContent>
                          {mappingOpts.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-medium ${chip.cls}`}
                          data-auditor={chip.auditor ? "1" : "0"} data-testid={`coa-source-${c.id}`}>{chip.label}</span>
                  </td>
                  <td className="px-3 py-2">
                    {c.fs_mapping ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-mono dark:bg-slate-800"
                            data-testid={`coa-fs-${c.id}`}>
                        {c.fs_mapping}
                      </span>
                    ) : (
                      <Link to={`/ifrs/${engagementId}/mapping`}
                            className="text-[11px] font-medium text-red-700 hover:underline dark:text-red-300"
                            data-testid={`coa-fs-unmapped-${c.id}`}>
                        {t("coaMapping.unmappedSet")}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {c.note_mapping ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-mono dark:bg-slate-800"
                              data-testid={`coa-note-map-${c.id}`}>
                          {c.note_mapping}
                        </span>
                        {c.note_mapping_source === "auto-inferred" && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50"
                                title={t("coaMapping.autoReviewTooltip")}
                                data-testid={`coa-note-src-auto-${c.id}`}>
                            {t("coaMapping.autoReview")}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Input value={c.note_reference || ""}
                           placeholder={t("coaMapping.noteRefPh")}
                           className="h-8 text-xs"
                           onChange={(e) => td.edit(c.id, "note_reference", e.target.value)}
                           data-testid={`coa-note-${c.id}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
        <TableSaveBar draft={td} lang={lang} testid="coa-table-save" />
      </CardContent>

      {/* Bulk apply dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("coaMapping.bulkDialogTitle").replace("{rows}", fmtPlural(selected.size, lang, dict.plurals.rows))}</DialogTitle>
            <DialogDescription>
              {t("coaMapping.bulkDialogDescPre")}<b>{t("coaMapping.bulkDialogDescBold")}</b>{t("coaMapping.bulkDialogDescPost")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("coaMapping.majorCategoryLabel")}</label>
              <Select value={bulk.major}
                      onValueChange={(v) => setBulk({ major: v, mapping: "" })}>
                <SelectTrigger data-testid="coa-bulk-major"><SelectValue placeholder={t("coaMapping.pickDash")} /></SelectTrigger>
                <SelectContent>
                  {taxonomy.major_categories.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("coaMapping.mappingLabel")}</label>
              <Select value={bulk.mapping} disabled={!bulk.major}
                      onValueChange={(v) => setBulk({ ...bulk, mapping: v })}>
                <SelectTrigger data-testid="coa-bulk-mapping">
                  <SelectValue placeholder={bulk.major ? t("coaMapping.pickDash") : t("coaMapping.pickCategoryFirst")} />
                </SelectTrigger>
                <SelectContent>
                  {asArray(taxonomy.mapping_options[bulk.major]).map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>{t("coaMapping.cancel")}</Button>
            <Button onClick={applyBulk} disabled={applying || !bulk.major || !bulk.mapping}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="coa-bulk-apply-confirm">
              {applying ? t("coaMapping.applying") : t("coaMapping.applyToN").replace("{n}", selected.size)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI suggest dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-fuchsia-600" /> {t("coaMapping.aiDialogTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("coaMapping.aiDialogDesc")}
            </DialogDescription>
          </DialogHeader>
          {aiLoading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("coaMapping.classifying")}
            </div>
          )}
          {!aiLoading && aiSug.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">{t("coaMapping.noSuggestionsReturned")}</div>
          )}
          {!aiLoading && aiSug.length > 0 && (
            <div className="max-h-[500px] overflow-y-auto rounded-md border">
              <ResizableTable cols={colW2} className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <ResizableTh index={0} className="w-10 px-2 py-2"></ResizableTh>
                    <ResizableTh index={1} className="px-3 py-2 text-start">{t("coaMapping.aiColAccount")}</ResizableTh>
                    <ResizableTh index={2} className="px-3 py-2 text-start">{t("coaMapping.aiColSuggested")}</ResizableTh>
                    <ResizableTh index={3} className="px-3 py-2 text-end">{t("coaMapping.aiColConfidence")}</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {aiSug.map((s) => {
                    const acct = coa.find((c) => c.id === s.id);
                    const c = s.confidence;
                    const conf = Math.round(c * 100);
                    const cls = c >= 0.85 ? "bg-emerald-100 text-emerald-800"
                              : c >= 0.6 ? "bg-amber-100 text-amber-800"
                              : "bg-red-100 text-red-800";
                    return (
                      <tr key={s.id} className="border-b" data-testid={`ai-sug-${s.id}`}>
                        <td className="px-2 py-2 text-center">
                          <Checkbox checked={aiPicked.has(s.id)}
                                    onCheckedChange={() => setAiPicked((p) => {
                                      const n = new Set(p);
                                      if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
                                      return n;
                                    })}
                                    data-testid={`ai-pick-${s.id}`} />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{acct?.raw_account_name || "?"}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{acct?.raw_account_code || "—"}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{s.major_category}</div>
                          <div className="text-xs text-muted-foreground">{s.mapping}</div>
                          <div className="mt-0.5 text-[10px] italic text-slate-500">{s.rationale}</div>
                        </td>
                        <td className="px-3 py-2 text-end">
                          <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>{conf}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </ResizableTable>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>{t("coaMapping.cancel")}</Button>
            <Button onClick={acceptAI} disabled={aiLoading || aiPicked.size === 0}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="coa-ai-accept">
              {t("coaMapping.aiApplyBtn").replace("{n}", fmtPlural(aiPicked.size, lang, dict.plurals.suggestions))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
